import { useMutation, useQuery } from '@apollo/client';
import {
  CREATE_RECOVERY_PLAN,
  DELETE_RECOVERY_PLAN,
  FIND_ALL_RECOVERY_PLANS_BY_PLAN,
  UPDATE_RECOVERY_PLAN,
} from '../../../queries';
import { phoneFormatter, tableSortingMethods } from '../../../../../utils';

import { ColumnsType } from 'antd/es/table';

import TableV3, {
  ModifyVariablesArgs,
} from '../../../../../components/generic/Table/TableV3';
import { baseColumns } from './exportColumns';
import { formFields } from './formFields';
import QueryErrorBoundary from '../../../../../components/error-handling';

export type FindAllRecoveryPlansByPlan = {
  applicationName: string;
  drPlanInPlace: boolean;
  email: string;
  hostingLocation: string;
  phone: string;
  planOwnerfirstName: string;
  planOwnerlastName: string;
  recoverySLA: string;
  uuid: string;
};

interface DisasterRecoveryTableProps {
  readonly planUUID: string;
}

export function DisasterRecoveryTable({
  planUUID,
}: DisasterRecoveryTableProps) {
  const { data, loading, error } = useQuery(FIND_ALL_RECOVERY_PLANS_BY_PLAN, {
    variables: { uuid: planUUID },
    skip: !planUUID,
  });

  const [createGQL] = useMutation(CREATE_RECOVERY_PLAN, {
    refetchQueries: ['FindAllRecoveryPlansByPlan'],
  });
  const [updateGQL] = useMutation(UPDATE_RECOVERY_PLAN, {
    refetchQueries: ['FindAllRecoveryPlansByPlan'],
  });
  const [deleteGQL] = useMutation(DELETE_RECOVERY_PLAN, {
    refetchQueries: ['FindAllRecoveryPlansByPlan'],
  });

  const columns: ColumnsType<any> = [
    {
      key: 'applicationName',
      title: 'Application',
      width: 150,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.applicationName, b.applicationName),
      render: (_, { applicationName }) => (
        <span className="custom-table--column custom-table--column-primary">
          {applicationName}
        </span>
      ),
    },
    {
      key: 'recoverySLA',
      title: 'Recovery SLA (Hours)',
      width: 100,
      align: 'right',
      sorter: (a, b) =>
        tableSortingMethods.number(a.recoverySLA, b.recoverySLA),
      render: (_, { recoverySLA }) =>
        recoverySLA ? (
          <span className="custom-table--column">{recoverySLA}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'hostingLocation',
      title: 'Hosting Location',
      width: 100,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.hostingLocation, b.hostingLocation),
      render: (_, { hostingLocation }) =>
        hostingLocation ? (
          <span className="custom-table--column">{hostingLocation}</span>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'drPlanInPlace',
      title: 'DR Plan In Place?',
      width: 100,
      align: 'center',
      sorter: (a, b) =>
        tableSortingMethods.boolean(a.drPlanInPlace, b.drPlanInPlace),
      render: (_, { drPlanInPlace }) => (
        <span className="custom-table--column">
          {drPlanInPlace ? 'Yes' : 'No'}
        </span>
      ),
    },
    {
      key: 'planOwnerfirstName',
      title: 'Plan Owner',
      width: 150,
      align: 'left',
      sorter: (a, b) =>
        tableSortingMethods.string(a.planOwnerfirstName, b.planOwnerfirstName),
      render: (_, { planOwnerfirstName, planOwnerlastName }) =>
        planOwnerfirstName || planOwnerlastName ? (
          <div className="custom-table--column">
            {planOwnerfirstName} {planOwnerlastName}
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
    {
      key: 'email',
      title: 'Contact',
      width: 100,
      align: 'left',
      sorter: (a, b) => tableSortingMethods.string(a.email, b.email),
      render: (_, { email, phone }) =>
        email || phone ? (
          <div className="flex flex-col custom-table--column text-sm">
            <span>{email}</span>
            <span>{phoneFormatter(phone)}</span>
          </div>
        ) : (
          <span className="placeholder-text text-sm">Not Provided</span>
        ),
    },
  ];

  const modifyVariables = ({ values, type }: ModifyVariablesArgs) => {
    if (type === 'create') {
      return {
        ...values,
        drPlanInPlace: values.drPlanInPlace === 'true',
        recoverySLA: String(values.recoverySLA),
        plan: { uuid: planUUID },
      };
    }

    return {
      ...values,
      drPlanInPlace: values.drPlanInPlace === 'true',
      recoverySLA: String(values.recoverySLA),
    };
  };

  return (
    <QueryErrorBoundary error={error}>
      <TableV3
        title="Disaster Recovery Plans"
        columns={columns}
        data={data?.findAllRecoveryPlansByPlan}
        loading={loading}
        editable
        formFields={formFields}
        createGQL={createGQL}
        updateGQL={updateGQL}
        deleteGQL={deleteGQL}
        modifyVariables={modifyVariables}
        exportExcelColumns={baseColumns}
      />
    </QueryErrorBoundary>
  );
}
